import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { PenLine, Eye, EyeOff } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
      return;
    }
    API.get(`/auth/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setError(formatError(e) || 'Invitation not found'))
      .finally(() => setLoading(false));
  }, [token, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await API.post('/auth/accept-invite', { token, password });
      window.location.href = '/dashboard';
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Invalid invitation</h1>
          <p className="text-slate-500 text-sm mb-6">{error || 'This link may have expired or already been used.'}</p>
          <Link to="/login" className="text-blue-700 font-medium hover:underline">Go to sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-8 h-8 bg-blue-700 rounded-sm flex items-center justify-center">
            <PenLine size={16} className="text-white" />
          </div>
          <span className="font-semibold text-xl text-slate-900" style={{ fontFamily: 'Outfit' }}>SignatureFlow</span>
        </div>

        <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Accept invitation</h1>
        <p className="text-slate-500 text-sm mb-6">
          Welcome, {invite.name}. Set a password for <span className="font-medium text-slate-700">{invite.email}</span>.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm text-sm mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
            <div className="relative">
              <input
                type={showPwd ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white pr-10"
                placeholder="At least 8 characters"
              />
              <button type="button" onClick={() => setShowPwd((p) => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white py-2.5 rounded-sm text-sm font-medium disabled:opacity-60"
          >
            {submitting ? 'Creating account...' : 'Activate account'}
          </button>
        </form>
      </div>
    </div>
  );
}
